
// base stuff
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { v4 as generateUuid } from "uuid"

// apollo queries
import * as cdn from "@coredination/components"
import JobDetailQuery from "../../graphql/job/jobdetail.graphql"
import UpdateJob from "../../graphql/job/updatejob.graphql"
import CreateJob from "../../graphql/job/createjob.graphql"
import WorkReportsForJobQuery from "@/graphql/job/workReportsForJob.graphql"
import SetUserState from "../../graphql/job/setuserstate.graphql"
import GroupsQuery from "@/graphql/group/groups.graphql"
import ScheduleResource from "../../graphql/job/scheduleResource.graphql"
import CustomerBriefQuery from "@/graphql/customer/customerbrief.graphql"
import AssetBriefQuery from "@/graphql/asset/assetbrief.graphql"
import AssetTypeQuery from "@/graphql/asset/assettype.graphql"
import CustomerProjectBrief from "@/graphql/customerproject/customerprojectbrief.graphql"
import Contract from "@/graphql/job/contract.graphql"
import UserDetail from "@/graphql/user/userdetail.graphql"
import JobAssetQuery from "@/graphql/asset/jobasset.graphql"

import { CircularLoader } from "@coredination/components"
import { Formatting } from "../../util/formatting"
import moment from "moment"
import { navbar, NavbarOption } from "@/components/NavbarOptions.vue"
import modaldialog from "@/components/dialogs/modaldialog"
const dateFormat: any = new Formatting.DateFormatting()

@Component({
    apollo: {
        job: {
            query: JobDetailQuery,
            skip() {
                return !this.uuid
            },
            variables() {
                return { uuid: this.uuid }
            },
        },
        jobToCopy: {
            query: JobDetailQuery,
            skip() {
                return !this.copyFromUuid || this.copyFromUuid == 'undefined' || this.copied
            },
            variables() {
                return { uuid: this.copyFromUuid }
            },
            update(data) {
                var job = data.job
                var cleanJob = this.cleanCopy(job)
                this.job = cleanJob
                this.copying = true
                return cleanJob
            }
        },
        workReportsForJob: {
            query: WorkReportsForJobQuery,
            variables() {
                return { jobUuid: this.model.uuid }
            },
            skip() {
                return !this.model || !this.model.uuid
            },
        },
        contract: {
            query: Contract,
            skip() {
                return !this.model || !this.model.contractInfo  || !this.model.contractInfo.contractId
            },
            variables() {
                return { uuid: this.model.contractInfo.contractId.toString() }
            }
        },
        me: cdn.graphql.me,
        groups: GroupsQuery,
        customerProject: {
            query: CustomerProjectBrief,
            skip() {
                return !this.model || !this.model.customerProjectUuid
            },
            variables() {
                return { uuid: this.model.customerProjectUuid }
            }
        },
        customer: {
            query: CustomerBriefQuery,
            skip() {
                return !this.model || !this.model.customerUuid
            },
            variables() {
                return { uuid: this.model.customerUuid }
            }
        },
        asset: {
            query: AssetBriefQuery,
            skip() {
                return !this.model || !this.model.assetUuid
            },
            variables() {
                return { uuid: this.model.assetUuid }
            }
        },
        user: {
            query: UserDetail,
            skip() {
                return this.model.ordererFullName || !this.ordererId
            },
            variables() {
                return { uuid: this.ordererId }
            }
        }
    },
    filters: {
        capitalize(text: string) {
            text = text.toLowerCase()
            return text.charAt(0).toUpperCase() + text.slice(1)
        },
    },
    components: {
        CircularLoader,
        StateField: () => import("@/components/fields/StateField.vue"),
        // form fields
        TemplateSelectField: () => import("@/components/fields/TemplateSelectField.vue"),
        LabelField: () => import("@/components/fields/LabelField.vue"),
        PriorityField: () => import("@/components/fields/PriorityField.vue"),
        GroupField: () => import("@/components/fields/GroupField.vue"),
        CustomerField: () => import("@/components/fields/CustomerField.vue"),
        ActivityField: () => import("@/components/fields/ActivityField.vue"),
        ExcludedRequiredFieldsField: () => import("@/components/fields/ExcludedRequiredFieldsField.vue"),
        OrdererField: () => import("@/components/fields/OrdererField.vue"),
        FreeTextField: () => import("@/components/fields/FreeTextField.vue"),
        AssetField: () => import("@/components/fields/AssetField.vue"),
        JobReportList: () => import("@/components/misc/JobReportList.vue"),
        SeparateDateTimePickerField: () => import("@/components/fields/SeparateDateTimePickerField.vue"),
        DateTimePickerField: () => import("@/components/fields/DateTimePickerField.vue"),
        DocumentsField: () => import("@/components/fields/DocumentsField.vue"),
        DestinationsField: () => import("@/components/fields/DestinationsField.vue"),
        WorkTaskField: () => import("@/components/fields/WorkTaskField.vue"),
        ResourceList: () => import("@/components/misc/ResourceList.vue"),
        ItemsTable: () => import("@/components/misc/ItemsTable.vue"),
        CustomFieldsField: () => import("@/components/fields/CustomFieldsField.vue"),
        JobCustomFormsField: () => import("@/components/fields/JobCustomFormsField.vue"),
        MyJobStateField: () => import("@/components/fields/MyJobStateField.vue"),
        JobNotesField: () => import("@/components/fields/JobNotesField.vue"),
        SignatureField: () => import("@/components/fields/SignatureField.vue"),
        // dialogs
        ShareDialog: () => import("@/components/dialogs/ShareDialog.vue"),
        SelectJobStateDialog: () => import("@/components/dialogs/SelectJobStateDialog.vue"),
        ContactField: () => import("@/components/fields/ContactField.vue"),
        UnsavedChangesDialog: () => import("@/components/dialogs/UnsavedChangesDialog.vue"),
        ExcludedFieldsDialog: () => import("@/components/dialogs/ExcludedFieldsDialog.vue"),
        RequiredFieldsDialog: () => import("@/components/dialogs/RequiredFieldsDialog.vue"),
        InvoicedField: () => import("@/components/fields/InvoicedField.vue"),
        ScheduleMeField: () => import("@/components/fields/ScheduleMeField.vue"),
    },
    beforeRouteLeave(to, from, next) {
        const dis = this as JobForm
        if (modaldialog.preventNavigation()) {
            console.log("modal dialog says no to navigation")
            return next(false)
        }

        // Prevent leaving on unsaved changes
        if (
            dis.changed ||
            ((dis.undoModel.uuid || dis.undoModel.groupUuid) &&
                JSON.stringify(dis.undoModel) != JSON.stringify(dis.model))
        ) {
            dis.showUnsavedChanges = true
            dis.discardChangesNext = next
            return next(false)
        }

        next()
    },
})
export default class JobForm extends Vue {
    @Prop()
    uuid?: string
    @Prop()
    copyFromUuid?: string
    copied?: boolean = false
    copying?: boolean = false

    @Prop()
    new?: boolean

    me: any = {}
    job: any = {}
    jobToCopy?: any
    contract: any = {}
    workReportsForJob: any[] = []
    groups?: any[]
    errorRequiredFields: any[] = []
    scheduleMe = false
    selectingExcludedFields = false
    selectingRequiredFields = false
    queuedDocs: string[] = []
    minDate: any = ""
    user: any = {}
    setResourceAssetFromParams: boolean = false

    undoModel: any = {}
    changed = false
    showUnsavedChanges = false
    discardChangesNext: any = null

    customerProject?: any
    customer: any
    asset: any

    resourceUpdateKey = 0
    destinationKey = 0
    contactKey = 0

    get inhibitSubscription() {
        return localStorage && !!localStorage.getItem("inhibitSubscription")
    }

    get required(): any {
        return this.model && this.model.requiredFields
            ? this.model.requiredFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }
    get excluded(): any {
        return this.model && this.model.excludedFields
            ? this.model.excludedFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }

    created() {
        if (this.new) {
            var groupUuid: string = this.me.primaryGroupUuid
            var groupName: string = this.me.primaryGroupName
            if (groupUuid) {
                this.job.groupUuid = groupUuid
                this.job.groupName = groupName
            }
            this.fillModelFromRouteParams()
        }
    }

    mounted() {
        // Guard against leaving/reloading with unsaved changes
        window.onbeforeunload = () => {
            if (this.changed) return this.$t("unsavedChangesTitle")
        }
        if (!this.new) {
            this.$apollo.queries.job.refetch()
            this.$apollo.queries.workReportsForJob.refetch()
        }

        this.$root.$emit("disableTabNavigation")
    }

    @Watch("model")
    watchModel(newModel: any) {
        if (!this.undoModel.uuid || this.undoModel.uuid != newModel.uuid) {
            this.undoModel = JSON.parse(JSON.stringify(newModel))
        }
    }

    @Watch("model", { deep: true })
    deepWatchModel() {
        if (
            (this.undoModel.uuid || this.undoModel.groupUuid) &&
            JSON.stringify(this.undoModel) != JSON.stringify(this.model)
        ) {
            this.changed = true
        }
    }

    @Watch("me")
    @Watch("myResource")
    @Watch("job.state")
    watchOptions() {
        if (this.me != null) {
            navbar?.setOptions(this.options)
        }
    }

    @Watch("job.startTime")
    changedStartTime(event: any) {
        if (!event) this.minDate = ""
        var startTime = new Date(event)
        if (startTime) this.minDate = dateFormat.date(startTime, null)
    }

    canScheduleSelf() {
        return (
            this.hasGroupPermission("schedule_self") ||
            (this.hasGroupPermission("do_work") && this.me?.id == this.job?.creatorId) ||
            (this.hasGroupPermission("do_work") && this.hasGroupPermission("dispatch"))
        )
    }

    queueDocs(event: any) {
        this.job.documents.push(event)
        this.queuedDocs.push(event.uuid)
    }

    async autoSetFromCustomer(uuid: string) {
        if (!uuid){
            this.autoSetLocation();
            this.autoSetContact();
            return
        }
        try {
            const response = await this.$apollo.query({
                query: CustomerBriefQuery,
                variables: { uuid },
            });
            const customer = response.data.customer;
            this.autoSetLabels(customer);
            this.autoSetContact();
            this.autoSetLocation();
        } catch (error) {
            console.error("Error fetching customer brief:", error);
        }
    }

    async autoSetFromCustomerProject(uuid: string) {
        if (!uuid){
            this.autoSetLocation();
            this.autoSetContact();
            return
        }
        try {
            const response = await this.$apollo.query({
                query: CustomerProjectBrief,
                variables: { uuid },
            });
            const customerProject = response.data.customerProject;
            this.autoSetLabels(customerProject);
            this.autoSetContact();
            this.autoSetLocation();
        } catch (error) {
            console.error("Error fetching customer project brief:", error);
        }
    }

    async autoSetFromAsset(uuid: string) {
        if (!uuid){
            this.autoSetLocation();
            this.autoSetContact();
            return
        }
        try {
            const response = await this.$apollo.query({
                query: AssetBriefQuery,
                variables: { uuid },
            });
            const asset = response.data.asset;
            this.autoSetLabels(asset);
            this.autoSetContact();
            this.autoSetLocation();
        } catch (error) {
            console.error("Error fetching asset brief:", error);
        }
    }

    autoSetLabels(entity: any) {
        if (!entity?.labels) return
        entity.labels.forEach((label: string) => {
            if (!this.model?.labels.includes(label)) this.model.labels.push(label);
        });
    }

    autoSetContact() {
        var contacts = this.job?.contacts
        var index = -1
        var entity = this.model.assetUuid && this.asset?.jobContact ? this.asset :
            this.model.customerProjectUuid && this.customerProject?.jobContact ? this.customerProject :
            this.model.customerUuid && this.customer?.jobContact ? this.customer : null
        if (!entity?.jobContact) return
        for (let i = 0; i < contacts.length; i++) {
            const cont = contacts[i];
            if (cont.emailAddress == entity.jobContact.emailAddress && cont.name == entity.jobContact.name &&
            cont.phoneNo == entity.jobContact.phoneNo && cont.role == entity.jobContact.role)
                return
            if (cont.auto === true)
                index = i;
        }
        if (index == -1) {
            for (let i = contacts.length - 1; i >= 0; i--) {
                const cont = contacts[i];
                if (cont.emailAddress == null && cont.name == null && cont.phoneNo == null)
                    index = i;
            }
        }
        var newContact = {}
        if (index == -1) {
            contacts.push(newContact)
            index = contacts.length - 1
        }
        contacts[index] = JSON.parse(JSON.stringify(entity.jobContact))
        contacts[index].auto = true
        this.contactKey++
    }

    autoSetLocation() {
        var destinations = this.job?.destinations
        var autoDestination, emptyDest
        var entity = this.model.assetUuid && this.asset?.location ? this.asset :
            this.model.customerProjectUuid && this.customerProject?.location ? this.customerProject :
            this.model.customerUuid && this.customer?.location ? this.customer : null
        if (!entity?.location || !destinations) return
        for (let i = 0; i < destinations.length; i++) {
            const dest = destinations[i];
            if ( dest.location.placeName == entity.location.placeName ) // Selected customer location already exists on job, do nothing
                return
            if (!autoDestination && dest.auto === true)
                autoDestination = dest
        }
        for (let i = destinations.length - 1; i >= 0; i--) {
            const dest = destinations[i];
            if (dest.location == null || (dest.location.placeName == null &&
                dest.location.latitude == null && dest.location.longitude == null)) {
                emptyDest = dest
            } else {
                break
            }
        }
        var newDest = {}
        if (!autoDestination && !emptyDest) destinations.push(newDest)
        autoDestination = autoDestination || emptyDest || newDest
        autoDestination.location = JSON.parse(JSON.stringify(entity.location))
        autoDestination.locationDescription = entity.locationDescription
        autoDestination.auto = true
        this.destinationKey += 1
    }

    autoReferenceNo?: string
    @Watch('job.customerProjectUuid')
    @Watch('customerProject.referenceNo')
    setAutoReferenceNo() {
        if (!this.job) return;
        if (this.job.customerProjectUuid) {
            if (this.customerProject && (!this.job.referenceNo || this.job.referenceNo === this.autoReferenceNo)) {
                Vue.set(this.job, "referenceNo", this.customerProject.referenceNo)
                this.autoReferenceNo = this.customerProject.referenceNo;
            }
        } else if (this.job.referenceNo === this.autoReferenceNo) {
            Vue.set(this.job, "referenceNo", null)
            delete this.autoReferenceNo;
        }
    }

    get fabOptions() {
        return [
            {
                title: this.$t("jobFabDestination"),
                click: () => {
                    this.scrollIntoView(this.$refs.destinations);
                    (this.$refs.destinations as any).add()
                },
                if: () => !this.isJobClosed && (!this.excluded || !this.excluded.destination),
            },
            {
                title: this.$t("jobFabWorkItem"),
                click: () => {
                    this.scrollIntoView(this.$refs.work);
                    (this.$refs.work as any).add()
                },
                if: () => !this.isJobClosed && (!this.excluded || !this.excluded.workItems),
            },
            {
                title: this.$t("jobFabResource"),
                click: () => {
                    this.scrollIntoView(this.$refs.resources);
                    (this.$refs.resources as any).clickAddResource()
                },
                if: () =>
                    !this.isJobClosed &&
                    this.multipleResources &&
                    this.hasGroupPermission("dispatch") &&
                    (!this.excluded || !this.excluded.addResource),
            },
            {
                title: this.$t("jobFabItem"),
                click: () => {
                    this.scrollIntoView(this.$refs.items);
                    (this.$refs.items as any)?._setupState?.addItem()
                },
                if: () => !this.isJobClosed && this.editable && (!this.excluded || !this.excluded.items),
            },
            {
                title: this.$t("jobFabForm"),
                click: () => {
                    this.scrollIntoView(this.$refs.forms);
                    (this.$refs.forms as any).addForm()
                },
                if: () => !this.isJobClosed && this.hasPermission("create_forms") && this.editable && (!this.excluded || !this.excluded.forms),
            },
            {
                title: this.$t("jobFabContact"),
                click: () => {
                    this.scrollIntoView(this.$refs.contacts);
                    (this.$refs.contacts as any).add()
                },
                if: () => !this.excluded || !this.excluded.contact,
            },
            /*
            {
                title: this.$t("jobFabDocument"),
                click: () => (this.$refs.documents as any).addDocument(),
                if: () => (!this.excluded || !this.excluded.documents),
            },
            */
        ]
    }

    scrollIntoView(ref: any) {
        ref && ref.$el && (ref.$el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "center" })
    }

    get model() {
        return this.job
    }
    set model(updatedJob: any) {
        this.job = updatedJob
    }
    get resources() {
        return this.job ? this.job.resources : []
    }

    get editable(): boolean {
        // insert unneccesarily complex authorization logic here
        return (
            this.me &&
            this.me.permissions &&
            (this.me.permissions!.includes("edit_all_data") ||
                this.me.permissions.includes("admin_jobs") ||
                this.me.permissions.includes("create_jobs")) &&
            !this.isJobClosed
        )
    }

    get isJobClosed(): boolean {
        return this.jobState === "CLOSED" || this.job.attestedTimeStamp != null
    }

    get isPublishable(): boolean {
        return (
            this.isJobClosed &&
            (this.me.permissions!.includes("edit_all_data") || this.me.permissions.includes("attest_jobs"))
        )
    }

    get hasBeenInvoiced(): boolean {
        return this.job?.invoicedTimeStamp
    }

    get multipleResources(): boolean {
        return this.job?.autoAddResources
    }

    get scheduledTime(): string {
        var startTime: any = this.model.startTime && new Date(this.model.startTime)
        var endTime: any = this.model.endTime && new Date(this.model.endTime)

        var startDate: string = startTime && dateFormat.datePart(startTime, null)
        var endDate: string = endTime && dateFormat.datePart(endTime, null)

        var sameDate: boolean = startDate != null && endDate != null && startDate == endDate

        var returner: string = startDate
        if (startDate && !this.model.allDay) {
            var startHours: string = dateFormat.time(startTime)
            var endHours: string = endTime && dateFormat.time(endTime)
            if (sameDate) {
                returner += " " + startHours + " - " + endHours
            } else if (endDate) {
                returner += " " + startHours
                returner += " - " + endDate + " " + endHours
            } else {
                returner += " " + startHours
            }
        }

        return returner
    }

    get jobState(): string {
        return this.job?.state
    }

    get isDraft(): boolean {
        return this.jobState == "DRAFT"
    }

    get attested(): boolean {
        return this.job?.attestedTimeStamp
    }

    get showJobState(): boolean {
        return (
            this.isDraft ||
            this.isJobClosed ||
            !this.myResource ||
            (this.myResource &&
                this.resources &&
                this.resources.filter((resource: any) => resource && resource.userId).length > 1)
        )
    }

    get loading() {
        return (
            this.$apollo.queries.job.loading ||
            this.$apollo.queries.me.loading ||
            this.$apollo.queries.groups.loading ||
            this.saving
        )
    }

    get showReportWorkButton() {
        if (!this.myResource || !this.myResource.state) return false
        if (this.isJobClosed) return false
        if (
            this.stateAsEnum(this.myResource.state) == WorkState.DAY_FINISHED &&
            this.job.toBeReported &&
            !this.job.stateTracked
        )
            return true
        if (
            this.stateAsEnum(this.myResource.state) == WorkState.FINISHED &&
            this.job.toBeReported &&
            !this.hasReported(this.me.id)
        )
            return true
        return false
    }

    get groupConfiguration(): any {
        if (!this.groups || !this.job || !this.job.groupUuid) return false
        var group = this.groups?.find((group) => group.uuid === this.job.groupUuid)
        return group && group.configuration && JSON.parse(group.configuration)
    }

    get hideInternalContractFields(): any {
        return this.contract && this.isContractSupplier && this.contract?.shareInternalInformationWithSupplier === false
    }

    get isContractSupplier(): any {
        return this.contract && this.contract?.supplierGroupUuid == this.job?.groupUuid
    }

    get ordererId(): string {
        return this.job?.ordererId ? this.job.ordererId.toString() : (this.job.orderOrdererId ? this.job.orderOrdererId.toString() : (this.job.creatorId ? this.job.creatorId.toString() : this.me.id))
    }

    fillModelFromRouteParams() {
        const params: any = {
            ...this.$route.params,
            ...this.$route.query,
        }
        if ("me" in params && (params.me || params.me === null)) {
            this.scheduleMe = true
        }
        if ("today" in params && (params.today || params.today === null)) {
            Vue.set(this.job, "startTime", new Date().getTime())
        }
        if ("date" in params) {
            Vue.set(this.job, "startTime", moment(params.date).toDate().getTime())
        }
        if ("customer" in params) {
            this.$apollo
                .query({
                    query: CustomerBriefQuery,
                    variables: { uuid: params.customer },
                })
                .then((response) => {
                    var customer = response.data.customer
                    Vue.set(this.model, "customerUuid", customer.uuid)
                    Vue.set(this.model, "customerName", customer.name)
                    Vue.set(this.model, "customerNo", customer.customerNo)
                })
        }
        if ("asset" in params) {
            this.$apollo
                .query({
                    query: AssetBriefQuery,
                    variables: { uuid: params.asset },
                })
                .then(async (assetResponse) => {
                    var asset = assetResponse.data.asset
                    if (asset.assetTypeId) {
                        await this.$apollo
                            .query({
                                query: AssetTypeQuery,
                                variables: { uuid: asset.assetTypeId }
                            })
                            .then( (assetTypeResponse) => {
                                var assetType = assetTypeResponse.data.assetType
                                if (assetType && (assetType.vehicle || assetType.usableAsResource)) {
                                var resourceAsset = {
                                        assetTypeId: assetType.id,
                                        assetTypeName: assetType.name,
                                        assetTypeUuid: assetType.uuid,
                                        assetUuid: asset.uuid,
                                        assetName: asset.name,
                                        assetNo: asset.assetNo
                                    }
                                if (!this.job.resources) this.job.resources = []
                                if (this.job.resources.length == 0) {
                                    var resources = [ {
                                        state: 'UNASSIGNED',
                                        assets: [ resourceAsset ]
                                    }]
                                    Vue.set(this.job, "resources", resources)
                                } else {
                                    var resource = this.job.resources[0]
                                    if (!resource.assets) resource.assets = []
                                    resource.assets.push(resourceAsset)
                                }
                                this.setResourceAssetFromParams = true
                            } else {
                                Vue.set(this.job, "assetUuid", asset.uuid)
                                Vue.set(this.job, "assetName", asset.name)
                                Vue.set(this.job, "assetNo", asset.assetNo)
                            }
                            })
                            .catch((error) => {
                                console.error(error)
                            })
                    } else {
                        Vue.set(this.job, "assetUuid", asset.uuid)
                        Vue.set(this.job, "assetName", asset.name)
                        Vue.set(this.job, "assetNo", asset.assetNo)
                    }
                })
                .catch((error) => {
                    console.error(error)
                })
        }
        if ("customerproject" in params) {
            this.$apollo
                .query({
                    query: CustomerProjectBrief,
                    variables: { uuid: params.customerproject },
                })
                .then((response) => {
                    var customerProject = response.data.customerProject
                    Vue.set(this.model, "customerProjectUuid", customerProject.uuid)
                    Vue.set(this.model, "customerProjectName", customerProject.name)
                    Vue.set(this.model, "customerProjectNo", customerProject.projectNo)
                })
        }
    }

    myResource?: any = null
    @Watch("job")
    @Watch("job.resources", { deep: true })
    @Watch("me")
    setMyResource() {
        var me = this.me
        if (me && me.id && this.job && this.job.resources) {
            this.myResource = this.job.resources.find((resource: any) => {
                return me.id === resource.userId
            })
            setTimeout(() => {
                if (this.myResource && !this.scheduleMe) {
                    this.scheduleMe = true
                } else if (!this.myResource && this.scheduleMe) {
                    this.scheduleMe = false
                }
            }, 100)
        }
    }

    oldResourceStates: string[] = []
    get resourceStates() {
        var states = []
        for (var resource of this.resources) states.push(resource.state)
        if (states.length != 0 && this.oldResourceStates.length != 0)
            this.updateResourceStates(states, this.oldResourceStates)
        this.oldResourceStates = states
        return states
    }

    updateResourceStates(states: string[], oldStates: string[]) {
        const jobUuid = this.job.uuid
        for (var i = 0; i < this.resources.length; i++) {
            if (states[i] != oldStates[i]) {
                var variables = {
                    uuid: this.job.uuid,
                    state: this.resources[i].state,
                    userId: this.resources[i].userId,
                }
                return this.$apollo
                    .mutate({
                        mutation: SetUserState,
                        variables: variables,
                        update(proxy, response) {
                            // Invalidate cache - TODO there is probably a better way?
                            // taken from WorkReportEdit.vue, modified for jobs
                            const hackProxy = proxy as any
                            Object.keys(hackProxy.data.data).forEach((key) => {
                                if (key.match(/(myJobs|myWorkStats|myCurrentJob)/) || jobUuid && key.indexOf('JobListItem:' + jobUuid) >= 0) hackProxy.data.delete(key)
                            })
                        },
                    })
                    .then(async (response: any) => {})
            }
        }
    }

    hasPermission(permission: string) {
        if (this.me && this.me.permissions) {
            return this.me.permissions.includes(permission)
        }
        return false
    }

    hasGroupPermission(permission: string) {
        var membership = this.getGroupMembershipForJob()
        if (membership && membership.permissions) {
            return this.getGroupMembershipForJob().permissions.includes(permission)
        }
        return false
    }

    getGroupMembershipForJob() {
        if (this.me && this.me.groupMemberships && this.job && this.job.groupUuid) {
            return this.me.groupMemberships.find((membership: any) => {
                return membership.groupUuid === this.job.groupUuid
            })
        }
        return null
    }

    hasMultipleActiveGroups() {
        return this.me && this.me.groupMemberships && this.me.groupMemberships.filter((m: any) => m.active).length > 1
    }

    hasReported(userId: string): boolean {
        for (let i = 0; i < this.workReportsForJob.length; i++) {
            if (this.workReportsForJob[i].userId == userId) return true
        }
        return false
    }

    canEditResourceAsset() {
        return (
            (this.isScheduled(this.me) && this.hasGroupPermission("create_jobs")) || this.hasGroupPermission("dispatch")
        )
    }

    saveEdit() {
        this.save()
    }

    clearErrorRequired(field: string) {
        this.errorRequiredFields = this.errorRequiredFields.filter((e) => e !== field)
    }

    get errorRequired(): any {
        return this.errorRequiredFields
            ? this.errorRequiredFields.reduce((obj: any, key: string) => ((obj[key] = true), obj), {})
            : null
    }

    verifyRequired(input: any) {
        var errorRequiredFields: string[] = []
        if (input.requiredFields) {
            if (input.requiredFields.includes("title")) if (!input["title"]) errorRequiredFields.push("title")
            if (input.requiredFields.includes("activity"))
                if (!input["activityId"]) errorRequiredFields.push("activity")
            if (input.requiredFields.includes("customer"))
                if (!input["customerUuid"]) errorRequiredFields.push("customer")
            if (input.requiredFields.includes("customerProject"))
                if (!input["customerProjectUuid"]) errorRequiredFields.push("customerProject")
            if (input.requiredFields.includes("description"))
                if (!input["description"]) errorRequiredFields.push("description")
            if (input.requiredFields.includes("group")) if (!input["groupUuid"]) errorRequiredFields.push("group")
            if (input.requiredFields.includes("startTime"))
                if (!input["startTime"]) errorRequiredFields.push("startTime")
            if (input.requiredFields.includes("endTime")) if (!input["endTime"]) errorRequiredFields.push("endTime")
            if (input.requiredFields.includes("labels"))
                if (!input["labels"] || input["labels"].length == 0) errorRequiredFields.push("labels")
            if (input.requiredFields.includes("vehicle")) if (!input["vehicleId"]) errorRequiredFields.push("vehicle")
            if (input.requiredFields.includes("referenceNo")) if (!input["referenceNo"]) errorRequiredFields.push("referenceNo")
            if (input.requiredFields.includes("priority")) if (!input["priority"]) errorRequiredFields.push("priority")
        }
        if (this) this.errorRequiredFields = errorRequiredFields
        if (input.customFields && input.customFields.length && input.customFields.length > 0) {
            if (this.$refs && this.$refs.customFields && typeof this.$refs.customFields == "object") {
                var customFields = Object(this.$refs.customFields)
                if (!customFields.canBeSaved()) return false
            }
        }
        if (errorRequiredFields.length == 0) return true
        return false
    }
    
    verifyStartEnd(input: any){
        // @ts-ignore
        var startCleared = this.$refs.startTime?.timeCleared // @ts-ignore
        var endCleared = this.$refs.endTime?.timeCleared
        if (input.startTime && input.endTime) {
            if (startCleared && !endCleared) return "error_enter_start_time"
            if (!startCleared && endCleared) return "error_enter_end_time"
            if (input.endTime < input.startTime && !startCleared) return "error_end_before_start"
        }
        return ""
    }

    verify(input: any) {
        if (!this.verifyRequired(input)) return "required_field_error"
        return this.verifyStartEnd(input)
    }

    saving: boolean = false
    save() {
        var self: any = this
        if (!this.job.uuid) this.job.uuid = generateUuid()
        const uuid = this.job.uuid
        var input: any
        try {
            input = this.getInputObj(this.job)
            var errorMsg = this.verify(input)
            if (errorMsg) {
                ;(this as any).$toast.error(this.$t(errorMsg), { icon: 'warning' })
                return
            }
        } catch (error) {
            console.log("catch:")
            this.saving = false
            console.error(error)
            //this.errorToast("job", error.message)
            return
        }
        this.saving = true
        if (this.new || this.copying) {
            return this.$apollo
                .mutate({
                    mutation: CreateJob,
                    variables: {
                        input,
                        templateId: this.job.fromTemplateId
                    },
                    update(proxy, response) {
                        self.invalidateCache(proxy, response)
                    },
                })
                .then(async (response) => {
                    this.saving = false
                    //this.saveToast("job")
                    Object.assign(this.job, response.data.createJob)
                    this.changed = false
                    this.undoModel = {}
                    let uuid: string = this.job && this.job.uuid
                    if (!uuid) uuid = response.data.createJob.uuid
                    if (self.queuedDocs?.length) self.$refs.documents.attachQueuedDocs(uuid, self.queuedDocs)
                    else this.$root.$emit("replaceRoute", "/job/" + uuid)
                })
                .catch((error) => {
                    this.saving = false
                    console.error(error)
                    //this.errorToast("job", error.message)
                })
        }
        return this.$apollo
            .mutate({
                mutation: UpdateJob,
                variables: {
                    uuid,
                    input,
                },
                update(proxy, response) {
                    const cache = proxy as any
                    Object.keys(cache.data.data).forEach((key) => {
                        if (key.indexOf('event') >= 0) return
                        if (key.match(/(myWorkStats|JobResource)/) || key.indexOf(uuid) >= 0){
                                cache.data.delete(key)
                            }
                    })
                },
            })
            .then(async (response) => {
                this.saving = false
                //this.saveToast("job")
                Object.assign(this.job, response.data.updateJob)
                this.changed = false
                this.undoModel = {}
                this.$router.back()
            })
            .catch((error) => {
                this.saving = false
                console.log(error)
            })
    }

    getInputObj(model: any): any {
        var groupConfiguration: any = this.groupConfiguration
        // @ts-ignore
        this.model.allDay = this.model.startTime && this.$refs.startTime?.timeCleared || this.model.endTime && this.$refs.endTime?.timeCleared
        var inputObj: any = {
            fromTemplateId: model.fromTemplateId ? model.fromTemplateId : null,
            description: model.description && model.description.trim().length > 0 ? model.description : null,
            groupUuid: model.groupUuid || null,
            customerUuid: model.customerUuid || null,
            customerProjectUuid: model.customerProjectUuid || null,
            activityId: model.activityId || null,
            assetUuid: model.assetUuid || null,
            jobNo: model.jobNo || null,
            allDay: model.allDay || false,
            startTime: model.startTime ? new Date(model.startTime).getTime() : null,
            endTime: model.endTime ? new Date(model.endTime).getTime() : null,
            startedTimeStamp: model.startedTimeStamp ? new Date(model.startedTimeStamp).getTime() : null,
            endedTimeStamp: model.endedTimeStamp ? new Date(model.endedTimeStamp).getTime() : null,
            duration: model.duration || 0,
            excludedFields: model.excludedFields && model.excludedFields.length > 0 ? model.excludedFields : null,
            requiredFields: model.requiredFields && model.requiredFields.length > 0 ? model.requiredFields : null,
            ordererId: model.ordererId || null,
            labels: model.labels || null,
            items: model.items ? this.stripItems(model.items) : [],
            customFields: model.customFields ? this.stripTypeNameFromFields(model.customFields) : [],
            destinations: this.stripTypeNameFromDestinations(model.destinations) || [],
            workItems: model.destinations ? this.stripWorkItems(model.workItems) : [],
            contacts: this.stripContacts(model.contacts) || [],
            forms: this.stripTypeNameFromFields(model.forms) || null,
            resources: this.stripResources(model.resources) || [],
            state: this.new || this.copying
                ? groupConfiguration && groupConfiguration.save_draft_default === "true"
                    ? "DRAFT"
                    : "PUBLISHED"
                : model.state,
            title: model.title,
            toBeInvoiced: model.toBeInvoiced,
            workReportTemplateId: model.workReportTemplateId,
            referenceNo: model.referenceNo,
            internalFields: model.internalFields,
            priority: model.priority
        }
        if (this.new) {
            if (model.stateTracked != null) inputObj.stateTracked = model.stateTracked
            if (model.transitStatesUsed != null) inputObj.transitStatesUsed = model.transitStatesUsed
            if (model.multipleReports != null) inputObj.multipleReports = model.multipleReports
            if (model.toBeReported != null) inputObj.toBeReported = model.toBeReported
            if (model.autoAddResources != null) inputObj.autoAddResources = model.autoAddResources
            if (model.selfScheduled != null) inputObj.selfScheduled = model.selfScheduled
            if (model.itemDeliveryChecked != null) inputObj.itemDeliveryChecked = model.itemDeliveryChecked
            if (model.sendDeliveryNotification != null) inputObj.sendDeliveryNotification = model.sendDeliveryNotification
            if (model.workDescriptionCopiedToReport != null) inputObj.workDescriptionCopiedToReport = model.workDescriptionCopiedToReport
            if (model.priority != null) inputObj.priority = model.priority
        }
        return inputObj
    }

    changeExcludedFields(changed: string[]) {
        Vue.set(this.model, "excludedFields", changed)
        /*
        var excludedFields: string[] = []
        changed.forEach((field: string) => {
            excludedFields.push(field)
        })
        this.job.excludedFields = excludedFields
        */
    }

    changeRequiredFields(changed: string[]) {
        Vue.set(this.model, "requiredFields", changed)
        /*
        var requiredFields: string[] = []
        changed.forEach((field: string) => {
            requiredFields.push(field)
        })
        this.job.requiredFields = requiredFields
        */
    }

    // i hate this stupid hacky solution but i can't figure out a better solution
    // to the __typename-situation. screw it, i'll leave this until we've figured it out
    // What is better than a hacky solution? A build upon hacky solution becoming a permanent solution
    stripTypeNameFromFields(dirtyFields: any[]) {
        if (!dirtyFields || dirtyFields.length == 0) return null
        return dirtyFields.map((field: any) => {
            const { ["__typename"]: _, ...cleanFields } = field
            return cleanFields
        })
    }

    stripItems(items: any[]) {
        var copy = JSON.parse(JSON.stringify(items))
        copy.forEach((item: any) => {
            if (item.unit! === "HOURS") {
                if (item.quantity)
                    item.quantity =
                        typeof item.quantity === 'string' && item.quantity.length > 0
                            ? dateFormat.parseDuration(item.quantity) / 60
                            : item.quantity
                if (item.invoicedQuantity)
                    item.invoicedQuantity =
                        typeof item.quantity === 'string' && item.invoicedQuantity.length > 0
                            ? dateFormat.parseDuration(item.invoicedQuantity) / 60
                            : item.invoicedQuantity
                else if (item.invoicedQuantity === null || item.invoicedQuantity === undefined)
                    item.invoicedQuantity = item.quantity
                else item.invoicedQuantity = 0
            } else {
                if (item.quantity)
                    item.quantity = item.quantity != "" ? dateFormat.parseFloat(item.quantity) : item.quantity
                if (item.invoicedQuantity)
                    item.invoicedQuantity =
                        item.invoicedQuantity != ""
                            ? dateFormat.parseFloat(item.invoicedQuantity)
                            : item.invoicedQuantity
                else if (item.invoicedQuantity === null || item.invoicedQuantity === undefined)
                    item.invoicedQuantity = item.quantity
                else item.invoicedQuantity = 0
            }
            delete item.__typename
        })
        return copy
    }

    stripResources(resources: any[]) {
        if (!resources || resources.length == 0) return []
        var copy = JSON.parse(JSON.stringify(resources))
        copy.forEach((resource: any) => {
            resource.assets && resource.assets.forEach((asset: any) => {
                delete asset['__typename']
            })
            delete resource['id']
            delete resource['__typename']
            if (!resource.uuid) resource.uuid = generateUuid()
        })
        return copy
    }

    stripTypeNameFromDestinations(destinations: any[]) {
        if (!destinations || destinations.length == 0) return []
        var newDestinations: any[] = []
        for (var dest of destinations) {
            var destination = Object.assign({}, dest)
            delete destination.id
            delete destination.auto
            delete destination.__typename
            if (destination && destination.location) delete destination.location!.__typename
            destination.customFields = this.stripTypeNameFromCustomFields(destination.customFields)
            newDestinations.push(destination)
            dest.workItems = this.stripWorkItems(dest.workItems);
        }
        return newDestinations
    }

    stripWorkItems(workItems: any[]) {
        if (!workItems || workItems.length == 0) return []
        var cleanWorkItems: any[] = []
        workItems.forEach((item) => {
            delete item.id
            delete item.__typename
            if (item.description) cleanWorkItems.push(item)
        })
        return cleanWorkItems
    }

    stripContacts(contacts: any[]) {
        if (!contacts || contacts.length == 0) return []
        var cleanContacts: any[] = []
        contacts.forEach((contact) => {
            delete contact.auto
            delete contact.__typename
            cleanContacts.push(contact)
        })
        return cleanContacts
    }

    forceUpdateRef(component: string) {
        if(this.$refs && this.$refs[component]) {
            const ref = this.$refs[component] as Vue
            ref.$forceUpdate()
        }
    }

    stripTypeNameFromCustomFields(customFields: any[]) {
        if (!customFields || customFields.length == 0) return null
        return customFields.map((custom: any) => {
            const { ["__typename"]: _, ...cleanCustomFields } = custom
            return cleanCustomFields
        })
    }

    stateAsEnum(state: string) {
        return WorkState[state as keyof typeof WorkState] as unknown as WorkState
    }

    get options(): NavbarOption[] {
        if (!this.me || !this.me.permissions || !this.model || this.model.deleted) return []

        return [
            { icon: "done", action: this.save },
            {
                title: this.$t("excludedFields"),
                action: () => {
                    this.selectingExcludedFields = true
                },
            },
            {
                title: this.$t("requiredFields"),
                action: () => {
                    this.selectingRequiredFields = true
                },
            },
        ]
    }

    invalidateCache(proxy: any, response: any) {
        const hackProxy = proxy as any
        Object.keys(hackProxy.data.data).forEach((key) => {
            if (key.match(/(myJobs|myWorkStats|JobResource|JobListItem)/)) {
                hackProxy.data.delete(key)
            }
        })
    }
    replacedByTemplate: any = null
    handleReplacedFields(event: any) {
        this.replacedByTemplate = event
    }
    get iosWrapperClient() {
        return Vue.prototype.$iosWrapperClient
    }
    get shouldScheduleMe() {
        return "me" in this.$route.params || "me" in this.$route.query
    }

    isScheduled(user: any) {
        return (
            this.model.resources &&
            this.model.resources.find((r: any) => {
                return r.userId == user.id
            })
        )
    }

    cleanCopy(copy: any) {
        delete copy.uuid
        delete copy.version
        delete copy.creationTimeStamp
        delete copy.lastModified
        delete copy.startedTimeStamp;
        delete copy.endedTimeStamp;
        delete copy.invoicedTimeStamp;
        delete copy.attestedTimeStamp;
        delete copy.creatorId
        delete copy.state
        delete copy.jobNo
        delete copy.events;
        delete copy.attestedById;

        if (copy.workItems) {
            copy.workItems.forEach((workItem: any) => {
                delete workItem.id
                delete workItem.done
                delete workItem.doneByUserId
                delete workItem.doneTimeStamp
            })
        }
        if (copy.destinations) {
            copy.destinations.forEach((destination: any) => {
                if (destination.workItems)
                    destination.workItems.forEach((workItem: any) => {
                        delete workItem.id;
                        delete workItem.done;
                        delete workItem.doneByUserId;
                        delete workItem.doneTimeStamp;
                    })
                delete destination.id
            })
        }
        if (copy.resources) {
            copy.resources.forEach((resource: any) => {
                delete resource.id
                resource.uuid = generateUuid()
                if (resource.state != "ASSIGNED" && resource.state != "UNASSIGNED") resource.state = "SCHEDULED"
            })
        }
        if (copy.items) {
            copy.items.forEach((item: any) => {
                item.uuid = generateUuid()
                delete item.invoiceId;
                delete item.invoicedTimeStamp;
            })
        }
        if (copy.documents) {
            copy.documents.forEach((document: any) => {
                delete document.uuid
            })
        }
        if (copy.forms) {
            copy.forms.forEach((form: any) => {
                delete copy.formUuid
                delete copy.formState
            })
        }
        this.copied = true
        return copy
    }

    discardChanges() {
        this.changed = false
        this.discardChangesNext()
        setTimeout(() => {
            Object.assign(this.model, this.undoModel)
            this.undoModel = {}
            var promises: Promise<any>[] = []
            if (this.$apollo.queries.job && typeof this.$apollo.queries.job.refetch == "function") {
                this.$apollo.queries.job.refetch()
            }
        }, 100)
    }

    replacedEmptyResourceWithMe: boolean = false
    @Watch("scheduleMe")
    @Watch("job.fromTemplateId")
    changeScheduleMe() {
        if (!this.job.resources) return
        var myResource = this.job.resources.find((res: any) => res.userId == this.me.id)
        const jobUuid = this.job.uuid
        if (this.scheduleMe && !myResource) {
            var meResource: any = {
                userId: this.me.id,
                state: "SCHEDULED",
                fullName: this.me.fullName,
                userName: this.me.userName,
                emailAddress: this.me.emailAddress,
                phoneNo: this.me.phoneNo,
                vehicleId: this.me.inVehicleId,
                avatarUrl: this.me.avatarUrl,
            }
            if (!this.job.resources) this.job.resources = []
            var emptyResource = this.job.resources.find((res: any) => res.userId == null )
            if (emptyResource) {
                var index = this.job.resources.indexOf(emptyResource)
                this.job.resources.splice(index, 1)
                this.job.resources.splice(index, 0, meResource)
                this.replacedEmptyResourceWithMe = true
            } else if (this.job.resources.length == 1 && !this.multipleResources) {
                this.job.resources.splice(0, 1, meResource);
            } else {
                this.job.resources.push(meResource)
            }
            if (meResource.vehicleId) {
                var self = this
                return this.$apollo
                    .mutate({
                        mutation: JobAssetQuery,
                        variables: {
                            uuid: meResource.vehicleId
                        },
                        update(proxy, response) {
                            const hackProxy = proxy as any
                            Object.keys(hackProxy.data.data).forEach((key) => {
                                if (key.match(/(myJobs|myWorkStats|myCurrentJob)/) || jobUuid && key.indexOf('JobListItem:' + jobUuid) >= 0) hackProxy.data.delete(key)
                            })
                        },
                    })
                    .then(async (response: any) => {
                        var responseAsset = {
                                assetId: response.data.asset.id,
                                assetName: response.data.asset.name,
                                assetNo: response.data.asset.assetNo,
                                assetTypeId: response.data.asset.assetTypeId,
                                assetTypeName: response.data.asset.assetTypeName,
                                assetTypeUuid: response.data.asset.assetTypeUuid,
                                assetUuid: response.data.asset.uuid
                            }
                        if (!meResource.assets) meResource.assets = []
                        var assetIndex: number = meResource.assets.findIndex((asset: any) => asset.assetUuid === responseAsset.assetUuid);
                        if (assetIndex == -1) {
                            meResource.assets.push(responseAsset)
                        }
                        self.resourceUpdateKey++
                    })
            }
        } else if (!this.scheduleMe && this.job.resources && this.job.resources.length > 0 && myResource) {
            var index = this.resources.indexOf(myResource)
            this.resources.splice(index, 1)
            if (this.replacedEmptyResourceWithMe) {
                var emptyResource: any = {
                    assets: [],
                    emailAddress: null,
                    fullName: null,
                    state: "UNASSIGNED",
                    userId: null,
                    userName: null
                }
                this.job.resources.splice(index, 0, emptyResource)
            }
        }
    }
}

export enum WorkState {
    UNASSIGNED,
    DECLINED,
    ASSIGNED,
    SCHEDULED,
    ACCEPTED,
    INACTIVE,
    IN_TRANSIT,
    ON_STATION,
    STARTED,
    PAUSED,
    DAY_FINISHED,
    FINISHED,
    CANCELED,
}
